import React, { useRef } from 'react';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import axios from 'axios';
import * as conf from '../../../config/config';
import { Crud_Catalogos } from "../../../herramientas/Crud_Catalogos";
import { timeString } from "../../../herramientas/DateFormat";
import { onTime, ontimeReportes } from "../../../components/navieras/validaciones";

const PAGE_SIZE = 5000;
const FILE_SIZE = 50000;
const fileType =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const fileExtension = '.xlsx';

// Mapeo de columnas para plataformas especiales
const COLUMN_RENAMES = {
  arribo_direcion_carga: 'Llegada de Unidad a centro de carga',
  reporte_centro_carga: 'Reporte de Operador / reporte en centro de carga',
  asignacion_cortina_centro_carga: 'Asignación de Cortina en centro carga',
  inicio_de_carga: 'Inicio de carga',
  cita_programada: 'Cita en cliente',
  inicio_ruta: 'Salida de Centro de Carga/Inicio de ruta',
  arribo_cliente_lleno: 'Llegada a Cliente',
  hora_maxima_salida: 'Hora de máxima salida',
  fin_de_carga: 'Fin de carga',
  entrega_documentos_control_tower: 'Entrega de documentos a control tower',
  entrega_documentos: 'Entrega de documentos en cliente',
  asignacion_rampa: 'Asignación de Rampa',
  inicio_de_descarga: 'Inicio de Descarga',
  entrega_de_pods: 'Entrega de PODs',
  fin_de_descarga: 'Fin de Descarga',
  cita_programada_terminal: 'Cita en centro carga',
};

function buildUrl(opciones, pagina) {
  const base = 'informes/';
  const paginacion = `pagina/${pagina}/total/${PAGE_SIZE}/variables/${opciones.fechaInicio}/${opciones.fechaFin}/`;

  switch (opciones.funcionalidad.label) {
    case 'Reporte General Incidencias Terrestre':
      return base + 'incidenciasGeneralesTerrestre/' + paginacion +
        opciones.tempPlataformaT.map(e => e.value).join(',') + '/' +
        opciones.tempOperacionEstatus.map(e => e.value).join(',');
    case 'Reporte General Incidencias Naviera':
      return base + 'incidenciasGeneralesNaviera/' + paginacion +
        opciones.tempPlataformaN.map(e => e.value).join(',') + '/' +
        opciones.tempOperacionEstatus.map(e => e.value).join(',');
    case 'Reporte General Alertas Terrestre':
      return base + 'alertasGeneralesTerrestre/' + paginacion +
        opciones.tempPlataformaT.map(e => e.value).join(',') + '/' +
        opciones.tempAlertasTipo.map(e => e.value).join(',');
    case 'Reporte General Alertas Naviera':
      return base + 'alertasGeneralesNaviera/' + paginacion +
        opciones.tempPlataformaN.map(e => e.value).join(',') + '/' +
        opciones.tempAlertasTipo.map(e => e.value).join(',');
    case 'Reporte General Movimientos Terrestre':
      return base + 'movimientosGeneralesTerrestre/' + paginacion +
        opciones.tempPlataformaT.map(e => e.value).join(',') + '/' +
        opciones.tempOperacionEstatus.map(e => e.value).join(',') + '/' +
        opciones.tempMovimientoTipo.map(e => e.value).join(',');
    case 'Reporte General Movimientos Naviera':
      if (opciones.citaProgramada) {
        return base + 'movimientosGeneralesCitaProgramadaNaviera/' + paginacion +
          opciones.tempPlataformaN.map(e => e.value).join(',') + '/' +
          opciones.tempOperacionEstatus.map(e => e.value).join(',') + '/' +
          opciones.tempMovimientoTipo.map(e => e.value).join(',');
      }
      return base + 'movimientosGeneralesNaviera/' + paginacion +
        opciones.tempPlataformaN.map(e => e.value).join(',') + '/' +
        opciones.tempOperacionEstatus.map(e => e.value).join(',') + '/' +
        opciones.tempMovimientoTipo.map(e => e.value).join(',');
    case 'Reporte General Movimientos Patios':
      return base + 'movimientosGeneralesPatios/' + paginacion +
        opciones.tempPlataformaN.map(e => e.value).join(',') + '/' +
        opciones.tempOperacionEstatus.map(e => e.value).join(',') + '/' +
        opciones.tempMovimientoTipo.map(e => e.value).join(',');
    case 'Reporte General Entregas Terrestre':
      return base + 'entregasGeneralesTerrestre/' + paginacion +
        opciones.tempPlataformaT.map(e => e.value).join(',');
    case 'Reporte General Entregas Naviera':
      return base + 'entregasGeneralesNaviera/' + paginacion +
        opciones.tempPlataformaN.map(e => e.value).join(',');
    case "Reporte General GPS's acciones":
      return base + 'gpsAcciones/' + paginacion.slice(0, -1); // sin trailing slash extra
    case 'Notificaciones de eventos':
      return base + 'notificacionesEnviadas/' + paginacion +
        opciones.tempPlataformaN.map(e => e.value).join(',') + '/' +
        opciones.tempNotificacionesTipo.map(e => e.value).join(',') + '/';
    default:
      return null;
  }
}

function processRow(row, opciones, sinColumnas, plataformasEspeciales) {
  let newRow;
  if (typeof row === 'object') {
    newRow = row;
  } else {
    // Eliminar caracteres de control literales Y secuencias \u0000-\u001F escapadas
    let sanitized = row
      .replace(/[\x00-\x1F\x7F]/g, ' ')
      .replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
        const code = parseInt(hex, 16);
        if (code <= 0x1F || code === 0x7F) return ' ';
        return match;
      });
    try {
      newRow = JSON.parse(sanitized);
    } catch (e) {
      // Último recurso: eliminar todo lo que rompa JSON
      sanitized = row.replace(/[^\x20-\x7E\xA0-\uFFFF{}",:\[\]0-9.\-\+\\\/\s]/g, '');
      try {
        newRow = JSON.parse(sanitized);
      } catch (e2) {
        console.warn('No se pudo parsear fila:', row.substring(0, 200));
        return {};
      }
    }
  }

  if (opciones.funcionalidad.label === 'Reporte General Movimientos Naviera') {
    newRow.estadia = '';
    let tiempoT = 0;
    if (newRow.salida_cliente_vacio && newRow.arribo_cliente_lleno && newRow.movimiento_tipo === 'Importación') {
      tiempoT = new Date(newRow.salida_cliente_vacio).getTime() - new Date(newRow.arribo_cliente_lleno).getTime();
      newRow.estadia = timeString(tiempoT);
    } else if (newRow.salida_cliente_lleno && newRow.arribo_cliente_vacio && newRow.movimiento_tipo === 'Exportación') {
      tiempoT = new Date(newRow.salida_cliente_lleno).getTime() - new Date(newRow.arribo_cliente_vacio).getTime();
      newRow.estadia = timeString(tiempoT);
    }

    if (newRow.lista_gps != null) {
      newRow.principal = '';
      newRow.desechable = '';
      newRow.cinturon = '';
      newRow.respaldo = '';
      newRow.candado = '';
      newRow.gps_fijo = '';
      newRow.cuenta_espejo = '';
      newRow.lista_gps.split(' || ').forEach((gps) => {
        let dataGps = gps.split(': ');
        switch (dataGps[0]) {
          case 'Principal': newRow.principal = dataGps[1] + ' || ' + newRow.principal; break;
          case 'Desechable': newRow.desechable = dataGps[1] + ' || ' + newRow.desechable; break;
          case 'Cinturón': newRow.cinturon = dataGps[1] + ' || ' + newRow.cinturon; break;
          case 'Respaldo': newRow.respaldo = dataGps[1] + ' || ' + newRow.respaldo; break;
          case 'Candado': newRow.candado = dataGps[1] + ' || ' + newRow.candado; break;
          case ' GPS FIJO': newRow.gps_fijo = dataGps[1] + ' || ' + newRow.gps_fijo; break;
          case 'Cuenta espejo': newRow.cuenta_espejo = dataGps[1] + ' || ' + newRow.cuenta_espejo; break;
          default: break;
        }
      });
    }
  }

  if (opciones.funcionalidad.label === 'Reporte General Entregas Terrestre') {
    if (newRow.gps != null) {
      let listaGps = newRow.gps.split(' || ');
      newRow.gps1 = listaGps[0] || '';
      newRow.gps2 = listaGps[1] || '';
    }
    delete newRow['gps'];
  }

  delete newRow['lista_gps'];

  if (newRow.variables) {
    let variables = JSON.parse(newRow.variables);
    newRow.ejecutivo = variables && variables.ejecutivo ? variables.ejecutivo : '';
    newRow.referencia_chubb = variables && variables.referencia_chubb ? variables.referencia_chubb : '';
    newRow.load = variables && variables.load ? variables.load : '';
  }
  delete newRow['variables'];

  if (newRow.plataforma === "SAMSUNG_SEM") {
    newRow['ontime_cetro_carga']= ontimeReportes(newRow, 'cita_programada_terminal');//arribo centro de carga: citaProgramadaTerminal-arriboDireccionCarga
    newRow['ontime_salida']= ontimeReportes(newRow, 'hora_maxima_salida');//salida centro de carga: horaMaximaSalida-inicioRuta
    newRow['ontime_cliente']= ontimeReportes(newRow, 'cita_programada');//arribo en cliente: citaProgramada - e.arriboClienteLleno
  }

  sinColumnas.forEach((columna) => { delete newRow[columna]; });

  // Renombrar columnas si es plataforma especial (excepto Patios)
  if (plataformasEspeciales && opciones.funcionalidad.label !== 'Reporte General Movimientos Patios') {
    const renamedRow = {};
    Object.entries(newRow).forEach(([key, value]) => {
      renamedRow[COLUMN_RENAMES[key] || key] = value;
    });
    return renamedRow;
  }

  return newRow;
}

function generateAndDownloadExcel(datos, fileName) {
  const ws = XLSX.utils.json_to_sheet(datos);
  const wb = { Sheets: { datos: ws }, SheetNames: ['datos'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
}

export const ExportarReportesGenerales = ({
  total,
  opciones,
  sinColumnas,
  setLoading,
  setError,
  setProgreso,
}) => {
  const abortRef = useRef(null);

  const buscar = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    const totalPaginas = Math.ceil(total / PAGE_SIZE);
    const totalArchivos = Math.ceil(total / FILE_SIZE);
    const plataformasEspeciales = opciones?.tempPlataformaN?.some(
      p => p.value === 61 || p.value === 1316 || p.value === 1711
    );

    setLoading(true);
    setError(false);
    setProgreso({ actual: 0, total: totalArchivos, porcentaje: 0 });

    try {
      let datosAcumulados = [];
      let archivoActual = 0;

      for (let pagina = 0; pagina < totalPaginas; pagina++) {
        if (controller.signal.aborted) break;

        const url = buildUrl(opciones, pagina);
        if (!url) break;

        const porcentajeGlobal = Math.round((pagina / totalPaginas) * 100);
        setProgreso({ actual: archivoActual, total: totalArchivos, porcentaje: porcentajeGlobal });

        let returnVal;
        try {
          const token = sessionStorage.getItem('tok');
          const res = await axios.get(conf.api_raiz + url, {
            headers: { Authorization: 'Bearer ' + token },
            responseType: 'arraybuffer'
          });
          // Decodificar como UTF-8 y sanitizar
          const decoder = new TextDecoder('utf-8', { fatal: false });
          let raw = decoder.decode(res.data);
          // Eliminar caracteres de control (0x00-0x1F excepto \t\n\r, y 0x7F)
          raw = raw.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ' ');
          returnVal = JSON.parse(raw);
        } catch (fetchErr) {
          console.error('Error en fetch página ' + pagina, fetchErr);
          continue;
        }

        if (controller.signal.aborted) break;
        await new Promise(r => setTimeout(r, 200));

        if (returnVal && returnVal.content && returnVal.content.length > 0) {
          const datos = returnVal.content.map(row => {
            try {
              return processRow(row, opciones, sinColumnas, plataformasEspeciales);
            } catch (rowErr) {
              console.warn('Error procesando fila:', rowErr);
              return {};
            }
          }).filter(row => Object.keys(row).length > 0);
          datosAcumulados = datosAcumulados.concat(datos);
        }

        // Generar archivo cada 50000 registros o en la última página
        if (datosAcumulados.length >= FILE_SIZE || pagina === totalPaginas - 1) {
          if (datosAcumulados.length > 0) {
            archivoActual++;
            const currentFileName = opciones.funcionalidad.label + ' ' + archivoActual;
            generateAndDownloadExcel(datosAcumulados, currentFileName);
            datosAcumulados = [];
          }
        }
      }

      setProgreso({ actual: totalArchivos, total: totalArchivos, porcentaje: 100 });
    } catch (err) {
      if (!controller.signal.aborted) {
        console.log(err.response || 'Error desconocido: ' + err);
        setError(true);
      }
    } finally {
      setLoading(false);
      setProgreso(null);
      abortRef.current = null;
    }
  };

  return (
    <>
      <div className="row">
        <div className="d-flex flex-column align-items-end">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={buscar}
            disabled={abortRef.current !== null}
          >
            Exportar a Excel
          </button>
        </div>
      </div>
      <div className="row">
        <div className="d-flex flex-column align-items-end">
          <p className="font-weight-light font-italic">
            Se descargarán {Math.ceil(total / 50000)} archivo(s) de excel (el
            número máximo de registros por archivo es de 50,000). Consultas en páginas de {PAGE_SIZE}.
          </p>
        </div>
      </div>
    </>
  );
};
